import type { KeyboardEvent } from 'react';

const RADIO_NAV_KEYS = new Set(['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown']);

function isEnabledRadio(element: HTMLElement): boolean {
    return element.getAttribute('aria-disabled') !== 'true' && !element.hasAttribute('disabled');
}

/**
 * Roving tabindex + selection for a `role="radiogroup"` of tiles, matching the ARIA radio pattern:
 * arrows move to the next enabled radio, wrap, and activate it; Space activates a link radio.
 */
export function onRadioGroupKeyDown(event: KeyboardEvent<HTMLDivElement>): void {
    const target = event.target;
    if (event.key === ' ' && target instanceof HTMLAnchorElement && target.getAttribute('role') === 'radio') {
        event.preventDefault();
        target.click();
        return;
    }

    if (!RADIO_NAV_KEYS.has(event.key)) {
        return;
    }

    const radios = Array.from(event.currentTarget.querySelectorAll<HTMLElement>('[role="radio"]')).filter(
        isEnabledRadio
    );
    if (radios.length === 0) {
        return;
    }

    event.preventDefault();

    const direction = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1 : -1;
    const activeElement = event.target instanceof Element ? event.target.closest('[role="radio"]') : null;
    const activeIndex = activeElement instanceof HTMLElement ? radios.indexOf(activeElement) : -1;
    const checkedIndex = radios.findIndex((radio) => radio.getAttribute('aria-checked') === 'true');
    const currentIndex = activeIndex === -1 ? Math.max(checkedIndex, 0) : activeIndex;
    const next = radios[(currentIndex + direction + radios.length) % radios.length];

    if (!next || next === activeElement) {
        return;
    }

    next.focus();
    next.click();
}
